import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { services } from '../data/mock';
import * as Icons from 'lucide-react';

const Services = () => {
  const [selectedService, setSelectedService] = useState(services[0]);

  const getIcon = (iconName) => {
    const Icon = Icons[iconName];
    return Icon ? <Icon className="w-8 h-8" /> : null;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50/40 via-white to-gray-50/60" />
        
        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Enterprise Services That
            <span className="bg-gradient-to-r from-gray-900 to-orange-400 bg-clip-text text-transparent"> Drive Results</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            Comprehensive AI-first services covering every aspect of your enterprise technology needs.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card 
                key={service.id}
                className="p-8 hover:shadow-2xl transition-all duration-300 group cursor-pointer border-2 border-transparent hover:border-orange-200 bg-white"
                onClick={() => setSelectedService(service)}
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  {getIcon(service.icon)}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-3 mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle2 className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="inline-flex items-center gap-2 text-orange-600 font-medium group-hover:gap-3 transition-all">
                  Learn More
                  <ArrowRight className="w-4 h-4" />
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Service View */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Card className="p-12 bg-gradient-to-br from-white to-gray-50 border-2 border-orange-100">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center mb-6">
                  {getIcon(selectedService.icon)}
                </div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">{selectedService.title}</h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">{selectedService.description}</p>
                
                <h3 className="text-xl font-bold text-gray-900 mb-4">Key Capabilities:</h3>
                <ul className="space-y-3">
                  {selectedService.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-orange-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-700 text-lg">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-6">
                <Card className="p-6 bg-white border-l-4 border-orange-400">
                  <h4 className="font-bold text-gray-900 mb-2">Business Impact</h4>
                  <p className="text-gray-600">40-60% reduction in implementation time with AI-augmented delivery</p>
                </Card>
                <Card className="p-6 bg-white border-l-4 border-gray-400">
                  <h4 className="font-bold text-gray-900 mb-2">Expert Team</h4>
                  <p className="text-gray-600">Certified specialists with 10+ years average experience</p>
                </Card>
                <Card className="p-6 bg-white border-l-4 border-orange-400">
                  <h4 className="font-bold text-gray-900 mb-2">Delivery Model</h4>
                  <p className="text-gray-600">Agile methodology with continuous integration and deployment</p>
                </Card>
                
                <Link to="/contact">
                  <Button className="w-full bg-gray-900 hover:bg-gray-800 text-white py-6 text-lg rounded-xl shadow-lg mt-6">
                    Discuss Your Project
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Service Approach */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Service Delivery Approach
            </h2>
            <p className="text-xl text-gray-600">
              Proven methodology combining human expertise with AI acceleration
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Discovery & Design",
                description: "AI-powered analysis of your ecosystem, requirements gathering, and solution architecture design."
              },
              {
                step: "02",
                title: "Implementation",
                description: "Agile development with AI-augmented coding, continuous testing, and iterative delivery."
              },
              {
                step: "03",
                title: "Optimization & Support",
                description: "Performance tuning, user training, and ongoing managed services for continuous improvement."
              }
            ].map((phase, index) => (
              <Card key={index} className="p-8 bg-white hover:shadow-xl transition-all duration-300">
                <div className="text-5xl font-bold text-orange-400 mb-4">{phase.step}</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{phase.title}</h3>
                <p className="text-gray-600 leading-relaxed">{phase.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" />
        
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Accelerate Your Transformation?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Let's discuss how our services can deliver measurable results for your enterprise.
          </p>
          <Link to="/contact">
            <Button className="bg-orange-400 hover:bg-orange-500 text-white px-8 py-6 text-lg rounded-xl shadow-2xl">
              Request a Consultation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
